import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { FaEdit, FaTrash, FaStar, FaRegStar, FaVideo } from 'react-icons/fa';
import './Testimonials.css';

const LIMIT = 10;

const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['true', '1', 'yes', 'on'].includes(normalized);
  }
  return Boolean(value);
};

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;

  const backendBase = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
  if (!backendBase) return image;

  const normalizedPath = String(image).replace(/^\/+/, '');
  if (normalizedPath.startsWith('uploads/')) {
    return `${backendBase}/${normalizedPath}`;
  }

  return `${backendBase}/uploads/${normalizedPath}`;
};

const AllTestimonials = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [updating, setUpdating] = useState(false);

  const [filters, setFilters] = useState({
    is_active: 'all',
    project_id: '',
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const buildQuery = (pageNum = 1, selectedFilters = filters) => {
    const params = new URLSearchParams({
      page: String(pageNum),
      limit: String(LIMIT),
    });

    if (selectedFilters.is_active !== 'all') {
      params.set('is_active', selectedFilters.is_active);
    }

    const projectId = selectedFilters.project_id.trim();
    if (projectId) {
      params.set('project_id', projectId);
    }

    return params.toString();
  };

  const fetchTestimonials = async (pageNum = page, selectedFilters = filters) => {
    setLoading(true);
    try {
      const query = buildQuery(pageNum, selectedFilters);
      const res = await getRequest(`testimonials?${query}`);

      if (res?.data?.success) {
        setTestimonials(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.total || 0);
        setPage(res.data.page || pageNum);
      } else {
        toast.error(res?.data?.message || 'Failed to load testimonials');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials(1, filters);
  }, []);

  const applyFilters = () => {
    fetchTestimonials(1, filters);
  };

  const clearFilters = () => {
    const resetFilters = { is_active: 'all', project_id: '' };
    setFilters(resetFilters);
    fetchTestimonials(1, resetFilters);
  };

  const openEditModal = (item) => {
    setCurrentItem({
      ...item,
      name: item?.name || '',
      project_id: item?.project_id || '',
      message: item?.message || '',
      rating: Number(item?.rating) || 5,
      video_link: item?.video_link || '',
      is_active: normalizeBoolean(item?.is_active),
      newImage: null,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentItem(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file') {
      setCurrentItem((prev) => ({
        ...prev,
        newImage: files && files.length > 0 ? files[0] : null,
      }));
      return;
    }

    setCurrentItem((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!currentItem) return;

    if (!currentItem.name.trim() || !currentItem.message.trim()) {
      toast.error('Name and message are required');
      return;
    }

    const ratingInt = parseInt(currentItem.rating, 10);
    if (Number.isNaN(ratingInt) || ratingInt < 1 || ratingInt > 5) {
      toast.error('Rating must be between 1 and 5');
      return;
    }

    setUpdating(true);

    const payload = new FormData();
    payload.append('name', currentItem.name.trim());
    payload.append('project_id', currentItem.project_id?.trim() || '');
    payload.append('message', currentItem.message.trim());
    payload.append('rating', String(ratingInt));
    payload.append('video_link', currentItem.video_link?.trim() || '');
    payload.append('is_active', currentItem.is_active ? 'true' : 'false');

    if (currentItem.newImage) {
      payload.append('image', currentItem.newImage);
    }

    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const res = await patchRequest(`testimonials/${currentItem.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res?.data?.success) {
        toast.success('Testimonial updated successfully');
        closeModal();
        fetchTestimonials(page, filters);
      } else {
        toast.error(res?.data?.message || 'Update failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating testimonial');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Delete Testimonial?',
      text: `Are you sure you want to delete testimonial from "${name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
          const res = await deleteRequest(`testimonials/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (res?.data?.success) {
            toast.success('Testimonial deleted successfully');
            if (testimonials.length === 1 && page > 1) {
              fetchTestimonials(page - 1, filters);
            } else {
              fetchTestimonials(page, filters);
            }
          } else {
            toast.error(res?.data?.message || 'Delete failed');
          }
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Error deleting testimonial');
        }
      }
    });
  };

  const handlePrevPage = () => {
    if (page > 1) fetchTestimonials(page - 1, filters);
  };

  const handleNextPage = () => {
    if (page < totalPages) fetchTestimonials(page + 1, filters);
  };

  const renderStars = (rating) => {
    const numericRating = Number(rating) || 0;
    const stars = [];

    for (let i = 1; i <= 5; i += 1) {
      stars.push(
        i <= numericRating ? (
          <FaStar key={i} className="star-filled" />
        ) : (
          <FaRegStar key={i} className="star-empty" />
        )
      );
    }

    return <div className="stars">{stars}</div>;
  };

  if (loading) return <div className="text-center mt-5">Loading testimonials...</div>;

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4 d-flex justify-content-between align-items-center">
        <h4 className="font-weight-bold">Testimonials</h4>
        <button className="btn btn-success" onClick={() => navigate('/testimonials/add')}>
          + Add Testimonial
        </button>
      </div>

      <div className="testimonial-filters mx-4 mt-4">
        <div className="row">
          <div className="col-md-4 mt-2">
            <label htmlFor="filterProjectId">Project ID</label>
            <input
              id="filterProjectId"
              name="project_id"
              className="form-control"
              value={filters.project_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, project_id: e.target.value }))}
              placeholder="Filter by project_id"
            />
          </div>
          <div className="col-md-4 mt-2">
            <label htmlFor="filterStatus">Status</label>
            <select
              id="filterStatus"
              className="form-control"
              value={filters.is_active}
              onChange={(e) => setFilters((prev) => ({ ...prev, is_active: e.target.value }))}
            >
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="col-md-4 mt-2 d-flex align-items-end">
            <button className="btn btn-primary mr-2" onClick={applyFilters}>
              Apply
            </button>
            <button className="btn btn-light" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="testimonials-grid">
        {testimonials.map((item) => {
          const imageUrl = getImageUrl(item.image);

          return (
            <div key={item.id} className="testimonial-card">
              <div className="card-header d-flex justify-content-between align-items-start">
                <div>
                  <h5 className="mb-0">{item.name}</h5>
                  {item.project_id && <small className="text-muted">Project ID: {item.project_id}</small>}
                  {renderStars(item.rating)}
                </div>
                <div className="actions">
                  <button className="btn-icon edit" onClick={() => openEditModal(item)}>
                    <FaEdit />
                  </button>
                  <button className="btn-icon delete" onClick={() => handleDelete(item.id, item.name)}>
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="card-body">
                {imageUrl && (
                  <div className="testimonial-image-wrap">
                    <img src={imageUrl} alt={item.name} className="testimonial-image" />
                  </div>
                )}

                <p className="message">&quot;{item.message}&quot;</p>

                {item.video_link && (
                  <div className="video-link">
                    <FaVideo className="mr-1" />
                    <a href={item.video_link} target="_blank" rel="noopener noreferrer">
                      Watch Video
                    </a>
                  </div>
                )}

                <div className="status-badge">
                  <span className={normalizeBoolean(item.is_active) ? 'badge-active' : 'badge-inactive'}>
                    {normalizeBoolean(item.is_active) ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {testimonials.length === 0 && <div className="no-data">No testimonials found.</div>}
      </div>

      {
        testimonials.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-4 mx-4">
            <div>Showing {testimonials.length} of {totalItems} testimonials</div>
            <div>
              <button className="btn btn-secondary mx-1" onClick={handlePrevPage} disabled={page === 1}>
                Previous
              </button>
              <span className="mx-2">Page {page} of {totalPages}</span>
              <button className="btn btn-secondary mx-1" onClick={handleNextPage} disabled={page === totalPages}>
                Next
              </button>
            </div>
          </div>
        )
      }

      {
        showModal && currentItem && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Testimonial</h5>
                  <button type="button" className="close" onClick={closeModal}>
                    &times;
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="editName">Name</label>
                    <input
                      id="editName"
                      name="name"
                      className="form-control"
                      value={currentItem.name}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editProjectId">Project ID (optional)</label>
                    <input
                      id="editProjectId"
                      name="project_id"
                      className="form-control"
                      value={currentItem.project_id}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editRating">Rating (1-5)</label>
                    <select
                      id="editRating"
                      name="rating"
                      className="form-control"
                      value={currentItem.rating}
                      onChange={handleInputChange}
                    >
                      <option value="1">1 - Poor</option>
                      <option value="2">2 - Fair</option>
                      <option value="3">3 - Good</option>
                      <option value="4">4 - Very Good</option>
                      <option value="5">5 - Excellent</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="editMessage">Message</label>
                    <textarea
                      id="editMessage"
                      name="message"
                      rows="4"
                      className="form-control"
                      value={currentItem.message}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editVideo">Video Link (optional)</label>
                    <input
                      id="editVideo"
                      name="video_link"
                      className="form-control"
                      value={currentItem.video_link}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editImage">Image (optional)</label>
                    <input
                      id="editImage"
                      name="image"
                      type="file"
                      accept="image/*"
                      className="form-control-file"
                      onChange={handleInputChange}
                    />
                    {currentItem.newImage && (
                      <small className="text-muted d-block mt-1">Selected: {currentItem.newImage.name}</small>
                    )}
                    {!currentItem.newImage && currentItem.image && (
                      <div className="existing-image-preview mt-2">
                        <img src={getImageUrl(currentItem.image)} alt="Current testimonial" />
                      </div>
                    )}
                  </div>

                  <div className="form-group form-check">
                    <input
                      type="checkbox"
                      id="editActive"
                      name="is_active"
                      className="form-check-input"
                      checked={Boolean(currentItem.is_active)}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="editActive">
                      Active (show on website)
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={updating}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default AllTestimonials;
